import { createPortal } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { cva } from "class-variance-authority";
import { usePortalTarget } from "~/hooks";
import { IconButton } from "../IconButton";
import { BaseMobileDropdownChoice, PopupProps } from "./interfaces";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import cn from "classnames";

const META_CHOICE_ID = "__on_expand__";
const DEFAULT_BUTTON_HEIGHT = 26;
const DEFAULT_POS = 20;
const ITEM_HEIGHT = 32;
const BORDER_HEIGHT = 2;

const getChoicesAndIdx = <Row extends BaseMobileDropdownChoice>(
    choices: Row[],
    value: Row | null,
    expandable: boolean,
) => {
    const finalChoices = choices.slice();
    const length = finalChoices.length + (expandable ? 1 : 0);
    let idx = value ? finalChoices.findIndex((c) => c.id === value.id) : -1;

    if (idx === -1 && value) {
        idx = Math.floor((length + 1) / 2);

        const old = finalChoices[idx];

        finalChoices[idx] = value;

        if (old) {
            finalChoices.push(old);
        }
    } else if (idx === -1) {
        idx = Math.floor(length / 2);
    }

    if (expandable) {
        finalChoices.push({
            id: META_CHOICE_ID,
        } as Row);
    }

    return [finalChoices, idx] as [Row[], number];
};

const getSafeArea = () => {
    const top =
        parseInt(
            document.documentElement.style.getPropertyValue("--safe-top"),
            10,
        ) || 0;
    const bottom =
        parseInt(
            document.documentElement.style.getPropertyValue("--safe-bottom"),
            10,
        ) || 0;

    return [top, bottom] as const;
};

export const listVariants = cva(
    "absolute border bg-white transition-all duration-200",
    {
        variants: {
            variant: {
                compact: "rounded border-gray-200",
                classic: "rounded-3xl border-gray-300",
                flat: "rounded-3xl border-gray-300",
            },
        },
        defaultVariants: {
            variant: "compact",
        },
    },
);

const Popup = <Row extends BaseMobileDropdownChoice>({
    show,
    choices: initialChoices,
    value,
    onChange,
    buttonRect,
    onExpand,
    onClose,
    renderOption = (choice) => choice.id,
    expandLabel = "See all choices",
    variant,
}: PopupProps<Row>) => {
    const target = usePortalTarget();
    const isVisible = useSignal(show.peek());
    const ref = useRef<HTMLDivElement>(null);
    const closeButtonContainerRef = useRef<HTMLDivElement>(null);
    const selected = useRef<Row | null>(null);
    const list = useComputed(
        () => (
            void show.value, getChoicesAndIdx(initialChoices, value, !!onExpand)
        ),
    );

    useSignalEffect(() => {
        if (show.value) {
            isVisible.value = true;
        }
    });

    useEffect(
        () => {
            const view = ref.current;
            const closeButton = closeButtonContainerRef.current;
            const container = view?.querySelector<HTMLElement>(
                "[data-dropdown-list]",
            );

            if (!view || !container || !closeButton) {
                return;
            }

            const [safeTop] = getSafeArea();
            const [choices, idx] = list.peek();
            const itemHeight = ITEM_HEIGHT;
            const margin = 8;
            const r = buttonRect.peek();
            const closeButtonHeight = closeButton.scrollHeight;

            const left = r?.left ?? DEFAULT_POS;
            const width = r?.width ?? window.innerWidth - DEFAULT_POS * 2;
            const height = choices.length * itemHeight + BORDER_HEIGHT;

            const vft = r?.top ?? DEFAULT_POS;
            const vfh = r?.height ?? DEFAULT_BUTTON_HEIGHT;

            const gft = vft + vfh / 2 - itemHeight / 2 - idx * itemHeight;
            const gtt =
                gft + height + margin > window.innerHeight
                    ? window.innerHeight - height - margin
                    : gft < safeTop + margin + closeButtonHeight
                      ? safeTop + margin + closeButtonHeight
                      : gft;

            const cft = gft - vft;
            const cfl = 0;
            const ctt = 0 + closeButtonHeight;

            const vtt = gtt - closeButtonHeight;
            const vth = height + closeButtonHeight;

            if (show.peek()) {
                view.style.left = `${left}px`;
                view.style.width = `${width}px`;
                view.style.top = `${vft}px`;
                view.style.height = `${vfh}px`;

                container.style.left = `${cfl}px`;
                container.style.width = `${width}px`;
                container.style.top = `${cft}px`;
                container.style.height = `${height}px`;
                container.style.opacity = "0";

                closeButton.style.opacity = "0";

                void container.getBoundingClientRect();

                view.style.top = `${vtt}px`;
                view.style.height = `${vth}px`;
                container.style.top = `${ctt}px`;
                container.style.opacity = "1";
                closeButton.style.opacity = "1";
            } else {
                view.style.top = `${vft}px`;
                view.style.height = `${vfh}px`;
                container.style.top = `${cft}px`;
                container.style.opacity = "0";
                closeButton.style.opacity = "0";
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [show.value],
    );

    useEffect(() => {
        const handleTouchEnd = (e: Event) => {
            if (!show.peek()) {
                return;
            }

            const view = ref.current;

            if (view && e.target instanceof Node && !view.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };

        document.addEventListener("touchend", handleTouchEnd, {
            passive: false,
        });

        return () => {
            document.removeEventListener("touchend", handleTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!target.value || (!isVisible.value && !show.value)) {
        return null;
    }

    const [choices] = list.value;

    return createPortal(
        <div
            ref={ref}
            className="fixed z-[70] overflow-hidden transition-all duration-200"
            onTransitionEnd={(e) => {
                if (e.propertyName === "top" && !show.peek()) {
                    isVisible.value = false;

                    if (selected.current) {
                        onChange(selected.current);
                        selected.current = null;
                    }
                }
            }}
        >
            <div
                ref={closeButtonContainerRef}
                className="absolute top-0 flex h-14 w-full items-center justify-end"
            >
                <IconButton
                    title="Close dropdown"
                    className="md:hidden"
                    onClick={onClose}
                >
                    <CloseIcon
                        width="18"
                        height="18"
                        className="h-[18px] w-[18px]"
                    />
                </IconButton>
            </div>
            <ul data-dropdown-list="" className={listVariants({ variant })}>
                {choices.map((choice) => (
                    <li key={choice.id}>
                        <button
                            type="button"
                            style={{
                                height: ITEM_HEIGHT,
                            }}
                            onClick={() => {
                                if (choice.id === META_CHOICE_ID) {
                                    onClose();
                                    onExpand?.();
                                    isVisible.value = false;
                                } else {
                                    selected.current = choice;
                                    onClose();
                                }
                            }}
                            className={cn(
                                "block w-full truncate",
                                variant === "compact"
                                    ? "first:rounded-t last:rounded-b"
                                    : "first:rounded-t-3xl last:rounded-b-3xl",
                                choice.id === META_CHOICE_ID
                                    ? "text-center font-bold text-gray-500"
                                    : "pl-4 text-left",
                                choice.id === value?.id && "text-sky-500",
                            )}
                        >
                            {choice.id === META_CHOICE_ID
                                ? expandLabel
                                : renderOption(choice)}
                        </button>
                    </li>
                ))}
            </ul>
        </div>,
        target.value,
    );
};

export default Popup;
